/**
 * CalendarReminderPopup — a full-screen centered modal that pops up when the
 * current user has a pending calendar reminder waiting in the bell.
 *
 * Polls GET /doserne/calendar/active-reminder every 20s. When the backend
 * returns a reminder notification, the modal appears over the entire page
 * with the event title / time / notes and four actions:
 *   • Dismiss          → marks the reminder as read
 *   • Snooze 5 min     → re-fires in 5 minutes
 *   • Snooze 15 min    → re-fires in 15 minutes
 *   • Open event       → navigates to the Email page (where the WeekCalendar
 *                        lives) and marks the reminder as read
 *
 * Visual only — no sound (as requested).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, Clock, X, Timer, ExternalLink, CalendarCheck, StickyNote, Lock, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "./i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 20000;

const KIND_ICON = { note: StickyNote, meeting: CalendarCheck, private: Lock, share: UsersIcon };
const KIND_RING = { cyan: "ring-cyan-300", amber: "ring-amber-300", rose: "ring-rose-300", emerald: "ring-emerald-300", violet: "ring-violet-300", slate: "ring-slate-300" };
const KIND_BG = { cyan: "bg-cyan-50 text-cyan-700", amber: "bg-amber-50 text-amber-700", rose: "bg-rose-50 text-rose-700", emerald: "bg-emerald-50 text-emerald-700", violet: "bg-violet-50 text-violet-700", slate: "bg-slate-100 text-slate-700" };

export default function CalendarReminderPopup({ authHeader }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [reminder, setReminder] = useState(null);
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const fetchOnce = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/calendar/active-reminder`, authHeader());
      const r = data?.reminder || null;
      // Keep the current modal if it's still valid; only replace when nothing
      // is showing yet so a user interaction isn't interrupted.
      setReminder((prev) => {
        if (prev && !seenIdsRef.current.has(prev.id)) return prev;
        return r;
      });
    } catch {
      // Silent — the user is likely signed out; the shell handles that.
    }
  }, [authHeader]);

  useEffect(() => {
    fetchOnce();
    timerRef.current = setInterval(fetchOnce, POLL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchOnce]);

  const dismiss = async () => {
    if (!reminder || busy) return;
    setBusy(true);
    seenIdsRef.current.add(reminder.id);
    try {
      await axios.post(`${API}/doserne/calendar/reminder/${reminder.id}/dismiss`, {}, authHeader());
      toast.success(t("cal.pop_dismissed"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setReminder(null);
      setBusy(false);
    }
  };

  const snooze = async (minutes) => {
    if (!reminder || busy) return;
    setBusy(true);
    seenIdsRef.current.add(reminder.id);
    try {
      await axios.post(`${API}/doserne/calendar/reminder/${reminder.id}/snooze?minutes=${minutes}`, {}, authHeader());
      toast.success(minutes === 5 ? t("cal.pop_snoozed_5") : t("cal.pop_snoozed_15"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setReminder(null);
      setBusy(false);
    }
  };

  const openEvent = async () => {
    if (!reminder || busy) return;
    setBusy(true);
    seenIdsRef.current.add(reminder.id);
    try {
      await axios.post(`${API}/doserne/calendar/reminder/${reminder.id}/dismiss`, {}, authHeader());
    } catch { /* ignore */ }
    const date = reminder.event_date || "";
    setReminder(null);
    setBusy(false);
    navigate(`/doserne/email?calendarDate=${encodeURIComponent(date)}`);
  };

  if (!reminder) return null;

  const kind = reminder.event_kind || "note";
  const color = reminder.event_color || "cyan";
  const KindIcon = KIND_ICON[kind] || StickyNote;
  const title = (reminder.title || "").replace(/^⏰\s*/, "");
  const date = reminder.event_date || "";
  const stime = reminder.event_start_time || "";
  const etime = reminder.event_end_time || "";
  const notes = reminder.event_notes || "";
  const prettyDate = (() => {
    try {
      const d = new Date(date + "T00:00:00");
      const loc = lang === "ro" ? "ro-RO" : lang === "no" ? "nb-NO" : "en-GB";
      return d.toLocaleDateString(loc, { weekday: "long", day: "numeric", month: "long" });
    } catch { return date; }
  })();

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="cal-reminder-popup">
      <div className={`w-full max-w-md bg-white rounded-3xl shadow-2xl ring-4 ${KIND_RING[color] || KIND_RING.cyan} ring-opacity-60 overflow-hidden animate-in zoom-in-95 fade-in`}>
        {/* Header strip */}
        <div className="relative px-5 pt-5 pb-4 bg-gradient-to-br from-amber-50 via-white to-cyan-50">
          <div className="flex items-center gap-2 text-amber-700">
            <Bell className="w-4 h-4" />
            <span className="text-[10px] font-black uppercase tracking-widest">{t("cal.pop_kicker")}</span>
          </div>
          <button type="button" onClick={dismiss} disabled={busy}
            className="absolute top-3 right-3 w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
            aria-label={t("cal.pop_dismiss")} data-testid="cal-pop-close">
            <X className="w-4 h-4" />
          </button>
          <div className="mt-2 flex items-start gap-3">
            <span className={`shrink-0 w-11 h-11 rounded-xl ${KIND_BG[color] || KIND_BG.cyan} flex items-center justify-center`}>
              <KindIcon className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-black text-slate-900 leading-tight break-words" data-testid="cal-pop-title">{title}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
                <span className="capitalize font-bold">{prettyDate}</span>
                {stime && (
                  <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-black">
                    <Clock className="w-3 h-3" />{stime}{etime ? ` – ${etime}` : ""}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        {notes && (
          <div className="px-5 py-3 border-t border-slate-100 max-h-40 overflow-y-auto">
            <div className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-1">{t("cal.notes_ph").replace("…", "")}</div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap">{notes}</div>
          </div>
        )}

        {/* Actions */}
        <div className="px-5 py-4 bg-slate-50/70 border-t border-slate-100 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => snooze(5)} disabled={busy} data-testid="cal-pop-snooze-5"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white hover:bg-amber-50 border border-slate-200 text-amber-700 text-xs font-black disabled:opacity-50">
            <Timer className="w-3.5 h-3.5" /> {t("cal.pop_snooze_5")}
          </button>
          <button type="button" onClick={() => snooze(15)} disabled={busy} data-testid="cal-pop-snooze-15"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white hover:bg-amber-50 border border-slate-200 text-amber-700 text-xs font-black disabled:opacity-50">
            <Timer className="w-3.5 h-3.5" /> {t("cal.pop_snooze_15")}
          </button>
          <button type="button" onClick={openEvent} disabled={busy} data-testid="cal-pop-open"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-black disabled:opacity-50">
            <ExternalLink className="w-3.5 h-3.5" /> {t("cal.pop_open")}
          </button>
          <button type="button" onClick={dismiss} disabled={busy} data-testid="cal-pop-dismiss"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-black disabled:opacity-50">
            <X className="w-3.5 h-3.5" /> {t("cal.pop_dismiss")}
          </button>
        </div>
      </div>
    </div>
  );
}
